import React, { useState, useEffect } from "react";
import {
  Box,
  Tabs,
  Tab,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  DialogContentText,
  Typography,
  Grid,
  Tooltip,
} from "@mui/material";
import { MoreVert, Edit, Delete, Add } from "@mui/icons-material";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import BrandSales from "src/Components/Charts/ActualVsExpectedMixChart/BrandSales";
import TwoColumnChart from "src/Components/Charts/TwoColumnChart/TwoColumnChart";
import SourceShareTable from "src/Components/Tables/SourceShareTable/SourceShareTable";
import TwoColumnVolumeChart from "src/Components/Charts/TwoColumnVolumeChart/TwoColumnVolumeChart";
import BarChartWob from "src/Components/Charts/ActualVsExpectedMixChart/BarChartWob";
import MarketComparisonTable from "src/Components/Tables/MarketComparison/MarketComparison";
import BarChartWobContribution from "src/Components/Charts/BarChartWobContribution/BarChartWobContribution";
import MarketGrowthContribution from "src/Components/Charts/MarketGrowthContribution/MarketGrowthContribution";
import SegmentsRetailerValueSalesChart from "src/Components/Charts/SegmentsRetailerValueSalesChart/SegmentsRetailerValueSalesChart";
import MarketContributionSegments from "src/Components/Charts/MarketContributionSegments/MarketContributionSegments";
import PriceElasticity from "src/Components/Charts/PriceElasticity/PriceElasticity";
import CannibalizationTable from "src/Components/Tables/CannibalizationTable/CannibalizationTable";
import SKUShareChart from "src/Components/Charts/SkuShareByBrand/SKUShareChart";
import CumulativeProductShareChart from "src/Components/Charts/CumulativeProductShareChart/CumulativeProductShareChart";
import dayjs from "dayjs";
import PrimaryButton from "src/Components/shared/shared/PrimaryButton";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import PPATable from "src/Components/Tables/PPATable/PPATable";
import MarketKPIsTable from "src/Components/Tables/MarketKPIsTable/MarketKPIsTable";
import MarketGrowthTable from "src/Components/Tables/MarketGrowthTable/MarketGrowthTable";
import PromotionsTable from "src/Components/Tables/PromotionsTable/PromotionsTable";
import PromoEffectivenessOverTimeChart from "src/Components/Charts/PromoEffectivenessOverTimeChart/PromoEffectivenessOverTimeChart";
import TopLossMakingPromotionsChart from "src/Components/Charts/TopLossMakingPromotionsChart/TopLossMakingPromotionsChart";
import PromotionUpliftBoxplot from "src/Components/Charts/PromotionUpliftBoxplot/PromotionUpliftBoxplot";
import { SortableChart } from "../StatisticsTest/components/SortableChart";


const Canvas = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [canvases, setCanvases] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [renameDialog, setRenameDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [newCanvasName, setNewCanvasName] = useState("");
  const [textDialogOpen, setTextDialogOpen] = useState(false);
  const [textDialogValue, setTextDialogValue] = useState("");
  const [textDialogMode, setTextDialogMode] = useState("add");
  const [textDialogType, setTextDialogType] = useState("CanvasText");
  const [editingChartId, setEditingChartId] = useState(null);
  const [dialogMode, setDialogMode] = useState("rename");
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const sensors = useSensors(useSensor(PointerSensor));

  const loadCanvases = () => {
    const stored = JSON.parse(localStorage.getItem("canvasData")) || {};
    console.log(stored)
    const formatted = Object.entries(stored).map(([name, charts]) => ({
      name,
      charts,
    }));
    setCanvases(formatted);
    console.log("Loaded canvases:", formatted);
  };

  useEffect(() => {
    loadCanvases();

    const handleStorageChange = (e) => {
      if (e.key === "canvasData") {
        loadCanvases();
      }
    };

    const handleCanvasUpdate = () => {
      loadCanvases();
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("canvasUpdated", handleCanvasUpdate);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("canvasUpdated", handleCanvasUpdate);
    };
  }, []);

  const renderComponentByName = (name, filters, chartId) => {
    console.log("[Canvas] renderComponentByName called with:", { name, filters, chartId });
    switch (name) {
      //Dashboard Charts
      case "MarketComparisonTable":
        return (
          <MarketComparisonTable
            {...filters}
            comparisonType={filters?.comparisonType}
            setComparisonType={() => {}}
          />
        );
      case "TwoColumnChart":
        return (
          <TwoColumnChart
            {...filters}
            salesTypeManager={filters?.twoColumnSalesType}
            setSalesTypeManager={() => {}}
            salesTypeRef={null}
          />
        );
      case "TwoColumnVolumeChart":
        return (
          <TwoColumnVolumeChart
            {...filters}
            salesTypeManager={filters?.twoColumnVolumeSalesType}
            setSalesTypeManager={() => {}}
            salesTypeRef={null}
          />
        );
      case "BrandSales":
        return (
          <BrandSales
            {...filters}
            timePeriod={filters?.brandSalesTimePeriod || "P12M"}
            selectedTab={filters?.brandSalesSelectedTab}
          />
        );
      case "BarChartWob":
        return (
          <BarChartWob
            {...filters}
            value={filters?.value}
            isManufacturer={filters?.isManufacturer}
            selected={filters?.selected}
          />
        );
      case "BarChartWobContribution":
        return (
          <BarChartWobContribution
            {...filters}
            value={filters?.value}
            isManufacturer={filters?.isManufacturer}
            selected={filters?.selected}
          />
        );
      case "MarketGrowthContribution":
        return (
          <MarketGrowthContribution
            selectedHierarchy={filters?.marketGrowthContributionSelectedHierarchy || "brand"}
            setSelectedHierarchy={(newHierarchy) => {
              updateChartFilter(chartId, "marketGrowthContributionSelectedHierarchy", newHierarchy);
            }}
          />
        );
      case "SegmentsRetailerValueSalesChart":
        return (
          <SegmentsRetailerValueSalesChart
            selectedHierarchy={filters?.segmentsRetailerValueSalesSelectedHierarchy || "brand"}
            setSelectedHierarchy={(newHierarchy) => {
              updateChartFilter(chartId, "segmentsRetailerValueSalesSelectedHierarchy", newHierarchy);
            }}
          />
        );
      case "MarketContributionSegments":
        return (
          <MarketContributionSegments
            selectedHierarchy={filters?.marketContributionSegmentsSelectedHierarchy || "brand"}
            setSelectedHierarchy={(newHierarchy) => {
              updateChartFilter(chartId, "marketContributionSegmentsSelectedHierarchy", newHierarchy);
            }}
          />
        );

      //Pricing & Architecture
      case "SourceShareTable":
        return (
          <SourceShareTable
            {...filters}
            selectedSku={filters?.sourceShareSelectedSku}
            setSelectedSku={() => {}}
          />
        );
      case "PriceElasticity":
        return (
          <PriceElasticity
            {...filters}
            selectedSkus={filters?.priceElasticitySelectedSkus}
            setSelectedSkus={() => {}}
          />
        );
      case "CannibalizationTable":
        return <CannibalizationTable />;
      case "MarketKPIsTable":
        return (
          <MarketKPIsTable 
            selectedHierarchy={filters?.marketKPIsSelectedHierarchy || "brand"} 
            setSelectedHierarchy={(newHierarchy) => {
              console.log('[Canvas] MarketKPIsTable hierarchy changed to:', newHierarchy);
              updateChartFilter(chartId, 'marketKPIsSelectedHierarchy', newHierarchy);
            }} 
          />
        );
      case "NewTable":
        return (
          <MarketGrowthTable
            selectedHierarchy={filters?.marketGrowthSelectedHierarchy || "brand"}
            setSelectedHierarchy={(newHierarchy) => {
              console.log('[Canvas] MarketGrowthTable hierarchy changed to:', newHierarchy);
              updateChartFilter(chartId, 'marketGrowthSelectedHierarchy', newHierarchy);
            }}
            startDate={filters?.marketGrowthStartDate ? dayjs(filters.marketGrowthStartDate) : undefined}
            setStartDate={(newDate) => {
              console.log('[Canvas] MarketGrowthTable startDate changed to:', newDate);
              updateChartFilter(chartId, 'marketGrowthStartDate', newDate?.toISOString());
            }}
            endDate={filters?.marketGrowthEndDate ? dayjs(filters.marketGrowthEndDate) : undefined}
            setEndDate={(newDate) => {
              console.log('[Canvas] MarketGrowthTable endDate changed to:', newDate);
              updateChartFilter(chartId, 'marketGrowthEndDate', newDate?.toISOString());
            }}
            selectedTab={filters?.marketGrowthSelectedTab || 0}
            setSelectedTab={(newTab) => {
              console.log('[Canvas] MarketGrowthTable selectedTab changed to:', newTab);
              updateChartFilter(chartId, 'marketGrowthSelectedTab', newTab);
            }}
            timePeriod={filters?.marketGrowthTimePeriod || "months"}
            setTimePeriod={(newTimePeriod) => {
              console.log('[Canvas] NewTable timePeriod changed to:', newTimePeriod);
              updateChartFilter(chartId, 'marketGrowthTimePeriod', newTimePeriod);
            }}
            target={filters?.marketGrowthTarget || 11}
            setTarget={(newTarget) => {
              console.log('[Canvas] NewTable target changed to:', newTarget);
              updateChartFilter(chartId, 'marketGrowthTarget', newTarget);
            }}
          />
        );

      //Assortment & Distribution
      case "SKUShareChart":
        return <SKUShareChart />;
      case "CumulativeProductShareChart":
        return (
          <CumulativeProductShareChart
            {...filters}
            brandSelection={filters?.cumulativeProductShareBrand}
            storeSelection={filters?.cumulativeProductShareStore}
            thresholdValue={filters?.cumulativeProductShareThreshold}
          />
        );
      case "PPATable":
        return (
          <PPATable
            selectedHier={filters?.selectedHier}
            selectedType={filters?.selectedType}
          />
        );

      //Promotions
      case "PromotionsTable":
        return <PromotionsTable />;
      case "PromoEffectivenessOverTimeChart":
        return <PromoEffectivenessOverTimeChart />;
      case "TopLossMakingPromotionsChart":
        return <TopLossMakingPromotionsChart />;
      case "PromotionUpliftBoxplot":
        return <PromotionUpliftBoxplot />;

      //Text Buttons
      case "CanvasTitle":
        return (
          <Box sx={{ display: "flex", alignItems: "center", px: 2, py: 1 }}>
            <Typography
              variant="h4"
              fontWeight="bold"
              sx={{ flexGrow: 1, margin: 0 }}
            >
              {filters?.text || "Untitled Title"}
            </Typography>
            <IconButton
              onClick={() =>
                openTextDialog("CanvasTitle", chartId, filters?.text)
              }
            >
              <Edit fontSize="small" />
            </IconButton>
          </Box>
        );

      case "CanvasText":
        return (
          <Box sx={{ px: 2, py: 1, width: "100%" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <Typography
                variant="body1"
                gutterBottom
                sx={{
                  textAlign: "left",
                  width: "100%",
                }}
              >
                {filters?.text || "Empty text block..."}
              </Typography>
              <IconButton
                onClick={() =>
                  openTextDialog("CanvasText", chartId, filters?.text)
                }
              >
                <Edit fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        );

      default:
        return <div>Component Not Found: {name}</div>;
    }
  };

  const updateChartFilter = (chartId, filterKey, filterValue) => {
    let chartUpdated = false;

    const updatedCanvases = canvases.map((canvas) => ({
      ...canvas,
      charts: canvas.charts.map((chart) => {
        if (chart.id !== chartId) return chart;
        chartUpdated = true;
        return { ...chart, filters: { ...chart.filters, [filterKey]: filterValue } };
      }),
    }));

    if (chartUpdated) {
      updateLocalStorage(updatedCanvases);
      setCanvases(updatedCanvases);
    }
  };

  const updateLocalStorage = (newCanvases) => {
    const updated = {};
    newCanvases.forEach((canvas) => {
      updated[canvas.name] = canvas.charts;
    });
    localStorage.setItem("canvasData", JSON.stringify(updated));

    window.dispatchEvent(new Event("canvasUpdated"));
    console.log("Updated localStorage:", updated);
  };

  const handleTabChange = (_, newValue) => {
    console.log("Tab changed to:", newValue);
    setSelectedTab(newValue);
  };

  const handleDeleteChart = (chartId) => {
    const updated = [...canvases];
    updated[selectedTab].charts = updated[selectedTab].charts.filter(
      (chart) => chart.id !== chartId
    );
    setCanvases(updated);
    updateLocalStorage(updated);
  };

  const handleAddCanvas = () => {
    const trimmedName = newCanvasName.trim();
    if (!trimmedName) return;

    if (canvases.some((c) => c.name === trimmedName)) {
      setSnackbarMessage("A canvas with this name already exists.");
      setShowSnackbar(true);
      return;
    }

    const newCanvas = { name: trimmedName, charts: [] };
    const updated = [...canvases, newCanvas];

    setCanvases(updated);
    setSelectedTab(updated.length - 1);
    updateLocalStorage(updated);
    setRenameDialog(false);
    setNewCanvasName("");
    setDialogMode("rename");
  };

  const handleMenuOpen = (event) => {
    console.log("Menu opened for tab:", selectedTab);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    console.log("Menu closed");
    setAnchorEl(null);
  };

  const handleRenameClick = () => {
    console.log("Rename clicked for canvas:", canvases[selectedTab]?.name);
    setDialogMode("rename");
    setNewCanvasName(canvases[selectedTab]?.name || "");
    setRenameDialog(true);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    console.log("Delete clicked for canvas:", canvases[selectedTab]?.name);
    setDeleteDialog(true);
    handleMenuClose();
  };

  const handleRenameConfirm = () => {
    const trimmedName = newCanvasName.trim();
    if (!trimmedName || selectedTab >= canvases.length) return;

    if (canvases.some((c, i) => i !== selectedTab && c.name === trimmedName)) {
      setSnackbarMessage("A canvas with this name already exists.");
      setShowSnackbar(true);
      return;
    }

    const updated = [...canvases];
    updated[selectedTab].name = trimmedName;
    setCanvases(updated);
    updateLocalStorage(updated);
    setRenameDialog(false);
    setNewCanvasName("");
  };

  const handleDeleteConfirm = () => {
    console.log("Delete confirm for tab:", selectedTab);
    if (selectedTab < canvases.length) {
      const updated = [...canvases];
      updated.splice(selectedTab, 1);
      setCanvases(updated);
      updateLocalStorage(updated);

      if (updated.length === 0) {
        setSelectedTab(0);
      } else if (selectedTab >= updated.length) {
        setSelectedTab(updated.length - 1);
      }
    }
    setDeleteDialog(false);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const current = [...canvases];
    const items = [...current[selectedTab].charts];
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);

    const [moved] = items.splice(oldIndex, 1);
    items.splice(newIndex, 0, moved);

    current[selectedTab].charts = items;
    setCanvases(current);
    updateLocalStorage(current);
  };

  const openTextDialog = (type, chartId = null, currentText = "") => {
    setTextDialogType(type);
    setTextDialogMode(chartId ? "edit" : "add");
    setTextDialogValue(currentText);
    setEditingChartId(chartId);
    setTextDialogOpen(true);
  };

  const handleTextDialogSave = () => {
    const updated = [...canvases];
    const text = textDialogValue.trim();

    if (textDialogMode === "add") {
      const newId = `${textDialogType}-${Date.now()}`;
      updated[selectedTab].charts.push({
        id: newId,
        componentName: textDialogType,
        filters: { text },
      });
    } else if (textDialogMode === "edit" && editingChartId) {
      const chart = updated[selectedTab].charts.find(
        (c) => c.id === editingChartId
      );
      if (chart) chart.filters.text = text;
    }

    setCanvases(updated);
    updateLocalStorage(updated);
    setTextDialogOpen(false);
    setTextDialogValue("");
    setEditingChartId(null);
  };

  const currentCharts = canvases[selectedTab]?.charts || [];

  // Single Dialog instance shared between the empty-state view and the main view.
  const canvasNameDialog = (
    <Dialog open={renameDialog} onClose={() => setRenameDialog(false)}>
      <DialogTitle>
        {dialogMode === "add" ? "Add New Canvas" : "Rename Canvas"}
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Canvas Name"
          fullWidth
          variant="outlined"
          value={newCanvasName}
          onChange={(e) => setNewCanvasName(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === "Enter") {
              dialogMode === "add" ? handleAddCanvas() : handleRenameConfirm();
            }
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setRenameDialog(false)}>Cancel</Button>
        <PrimaryButton
          onClick={dialogMode === "add" ? handleAddCanvas : handleRenameConfirm}
          variant="contained"
        >
          {dialogMode === "add" ? "Add" : "Rename"}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );

  if (canvases.length === 0) {
    return (
      <Box
        sx={{
          minHeight: "88vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 2,
          p: 2,
        }}
      >
        <Typography variant="h5" color="text.secondary">
          No canvases available yet.
        </Typography>

        <PrimaryButton
          variant="outlined"
          onClick={() => {
            setDialogMode("add");
            setNewCanvasName("");
            setRenameDialog(true);
          }}
          startIcon={
            <span style={{ fontSize: "20px", fontWeight: "bold" }}>+</span>
          }
          sx={{
            backgroundColor: "#eeeeee",
            color: "#333",
            border: "1px solid #ccc",
            textTransform: "none",
            fontWeight: 500,
            fontSize: "16px",
            px: 3,
            py: 1,
            "&:hover": {
              backgroundColor: "#e9e9e9",
            },
          }}
        >
          Add Canvas
        </PrimaryButton>

        {canvasNameDialog}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Tabs Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 2,

          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          sx={{ mr: 0.5 }}
        >
          {canvases.map((canvas, index) => (
            <Tab
              key={index}
              sx={{
                minWidth: "auto",
                px: 1,
                mr: 0.5,
              }}
              label={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <span>{canvas.name}</span>
                  {index === selectedTab && (
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuOpen(e);
                      }}
                      size="small"
                      sx={{ p: 0.25 }}
                      aria-label="Canvas options"
                    >
                      <MoreVert fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              }
            />
          ))}
        </Tabs>
        <Tooltip title="Add new canvas" arrow>
          <IconButton
            onClick={() => {
              setNewCanvasName("");
              setDialogMode("add");
              setRenameDialog(true);
            }}
            size="large"
            sx={{ flexShrink: 0 }}
            aria-label="Add new canvas"
          >
            <Add fontSize="large" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Text Buttons */}
      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <PrimaryButton
          variant="contained"
          onClick={() => openTextDialog("CanvasTitle")}
        >
          Add Title
        </PrimaryButton>
        <PrimaryButton
          variant="contained"
          onClick={() => openTextDialog("CanvasText")}
        >
          Add Text
        </PrimaryButton>
      </Box>

      <Dialog open={textDialogOpen} onClose={() => setTextDialogOpen(false)}>
        <DialogTitle>
          {textDialogMode === "add" ? "Add" : "Edit"}{" "}
          {textDialogType === "CanvasTitle" ? "Title" : "Text"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Content"
            fullWidth
            variant="outlined"
            value={textDialogValue}
            onChange={(e) => setTextDialogValue(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTextDialogOpen(false)}>Cancel</Button>
          <PrimaryButton onClick={handleTextDialogSave} variant="contained">
            Save
          </PrimaryButton>
        </DialogActions>
      </Dialog>

      {/* Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleRenameClick}>
          <Edit fontSize="small" sx={{ mr: 1 }} />
          Rename Canvas
        </MenuItem>

        <MenuItem onClick={handleDeleteClick}>
          <Delete fontSize="small" sx={{ mr: 1 }} />
          Delete Canvas
        </MenuItem>
      </Menu>

      {/* Rename / Add Canvas Dialog */}
      {canvasNameDialog}

      {/* Delete Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Delete Canvas</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the canvas? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Content Area */}
      {currentCharts.length === 0 ? (
        <Box
          sx={{
            minHeight: "75vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >
          <Typography variant="h5" color="text.secondary">
            No charts in this canvas yet.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Use &quot;Save to Canvas&quot; to add charts from your dashboard.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ mt: 2 }}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={currentCharts.map((c) => c.id)}
              strategy={verticalListSortingStrategy}
            >
              <Grid container spacing={2}>
                {currentCharts.map((chart) => {
                  const isTextElement =
                    chart.componentName === "CanvasTitle" ||
                    chart.componentName === "CanvasText";
                  return (
                  <Grid
                    item
                    xs={isTextElement ? 12 : chart.xs || 12}
                    sm={isTextElement ? 12 : 6}
                    md={isTextElement ? 12 : chart.md || 6}
                    key={chart.id}
                    sx={{ mb: 3 }}
                  >
                    <SortableChart
                      user={{
                        id: chart.id,
                        name: chart.componentName,
                        componentName: chart.componentName,
                        filters: chart.filters,
                        onHierarchyChange: (newHierarchy) => {
                          const key =
                            chart.componentName === "MarketGrowthContribution"
                              ? "marketGrowthContributionSelectedHierarchy"
                              : chart.componentName === "SegmentsRetailerValueSalesChart"
                                ? "segmentsRetailerValueSalesSelectedHierarchy"
                                : chart.componentName === "MarketContributionSegments"
                                  ? "marketContributionSegmentsSelectedHierarchy"
                                  : "selectedHierarchy";
                          updateChartFilter(chart.id, key, newHierarchy);
                        },
                        component: renderComponentByName(
                          chart.componentName === "PPATable" ? chart.id : chart.componentName,
                          chart.filters,
                          chart.id
                        ),
                      }}
                      RemoveComponent={() => handleDeleteChart(chart.id)}
                      showSaveButton={false}
                      showCloseTooltip={false}
                      showInsightsButton={false}
                    />
                  </Grid>
                );
                })}
              </Grid>
            </SortableContext>
          </DndContext>
        </Box>
      )}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={4000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        sx={{
          position: "fixed",
          zIndex: 9999,
        }}
      >
        <MuiAlert
          onClose={() => setShowSnackbar(false)}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default Canvas;
